import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, Button, CardHeader, CardTitle } from "reactstrap";
import ReactTable from "components/tables/ReactTable";
import { useHistory } from "react-router-dom";
import useRoute from "hooks/useRoute";
import { PATHS } from "constants/Constants";
import Swal from "sweetalert2";
/**
 * Renders the LoansTable component.
 * @param {Object[]} data - Array of loan application objects to display.
 * @param {boolean} hideViewDealButton - Optional prop to hide the "View Deal" button.
 * @returns {JSX.Element}
 */

const confirmAndHardDeleteApplication = async (appId, onSuccess) => {
	const result = await Swal.fire({
		title: "Delete Application?",
		text: "This will permanently delete the application. This cannot be undone.",
		icon: "warning",
		showCancelButton: true,
		confirmButtonText: "Delete",
		cancelButtonText: "Cancel",
		confirmButtonColor: "#dc3545", // Bootstrap danger
	});

	if (result.isConfirmed) {
		// 🔧 Mock API call
		console.log(`Mock deleting application ID: ${appId}`);

		// Simulate slight delay for realism
		setTimeout(() => {
			onSuccess(appId);
			Swal.fire("Deleted!", "The application has been removed.", "success");
		}, 500);
	}
};

const LoansTable = ({ data, hideViewDealButton = false }) => {
	const [showDeniedOnly, setShowDeniedOnly] = useState(false);
	const DENIED_STATUSES = useMemo(
		() => ["DECLINED", "CANCEL/DUPLICATE", "CANCEL/WITHDRAWN"],
		[]
	);

	// Routing to deal-detail page
	const history = useHistory();
	const dealDetailPage = useRoute(PATHS.DEAL_DETAIL);

	// Initialize table data
	const [tableData, setTableData] = useState(data || []);

	useEffect(() => {
		if (Array.isArray(data)) {
			const sortedData = [...data].sort(
				(a, b) => new Date(b.created_date) - new Date(a.created_date)
			);

			const filtered = sortedData.filter((row) => {
				const status = row.status?.trim()?.toUpperCase();
				if (showDeniedOnly) {
					return DENIED_STATUSES.includes(status);
				}
				return !DENIED_STATUSES.includes(status);
			});

			setTableData(filtered);
		}
	}, [data, showDeniedOnly, DENIED_STATUSES]);

	const hasDeniedApps = Array.isArray(data)
		? data.some((row) =>
				DENIED_STATUSES.includes(row.status?.trim()?.toUpperCase())
		  )
		: false;

	// Remove a row after successful withdrawal
	const handleDeleteSuccess = useCallback((deletedId) => {
		setTableData((prev) =>
			prev.filter((row) => row.submitted_app_id !== deletedId)
		);
	}, []);

	// Allowed status values and their corresponding button colors
	const statusColorMap = {
		PENDING: "outline-primary",
		PROCESSED: "primary",
		APPROVED: "success",
		"COUNTER OFFER": "outline-warning",
		"PAYMENT CALL": "info",
		DECLINED: "danger",
		"CANCEL/DUPLICATE": "secondary",
		"CANCEL/WITHDRAWN": "secondary",
	};

	// Default fallback status
	const DEFAULT_STATUS = "PENDING";

	/**
	 * Renders a status button with strict validation
	 * @param {{ value: any }} props - Cell renderer prop with status value
	 * @returns JSX.Element
	 */
	const statusCell = ({ value }) => {
		// Ensure value is a string before processing
		const isString = typeof value === "string";

		// Normalize if valid string, otherwise fallback to empty string
		const normalizedValue = isString ? value.trim().toUpperCase() : "";

		// Check if normalized value is a valid status
		const isValidStatus = Object.prototype.hasOwnProperty.call(
			statusColorMap,
			normalizedValue
		);

		// Final safe value to use
		const safeStatus = isValidStatus ? normalizedValue : DEFAULT_STATUS;
		const color = statusColorMap[safeStatus];

		// Human-friendly display label
		let displayValue;
		switch (safeStatus) {
			case "CANCEL/DUPLICATE":
				displayValue = "CANCEL / DUPLICATE";
				break;
			case "CANCEL/WITHDRAWN":
				displayValue = "CANCEL / WITHDRAWN";
				break;
			default:
				displayValue = safeStatus;
		}

		return (
			<Button
				size="lg"
				className="no-hover w-90 h-90"
				style={{
					fontSize: ".8rem",
					textWrap: "wrap",
					cursor: "auto",
				}}
				color={color}
			>
				{displayValue}
			</Button>
		);
	};
	// Hidden App ID in lender cell

	const lenderCell = ({ row }) => (
		<span>
			{row.original.lender_id}
			<span style={{ display: "none" }}>{row.original.submitted_app_id}</span>
		</span>
	);

	const dateCell = ({ row }) => {
		const date = new Date(row.original.created_date);

		const formattedDate = date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});

		const formattedTime = date.toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});

		return (
			<div style={{ textAlign: "center", lineHeight: "1.4" }}>
				<div>{formattedDate}</div>
				<div>{formattedTime}</div>
			</div>
		);
	};

	// Borrower name on two lines
	const borrowerCell = ({ row }) => {
		const { FirstName, LastName } = row.original.borrower_extra;
		return (
			<div style={{ lineHeight: "1.2" }}>
				<div>{FirstName}</div>
				<div>{LastName}</div>
			</div>
		);
	};

	// Co-borrower name on two lines
	const coborrowerCell = ({ row }) => {
		const first = row?.original?.coborrower_extra?.FirstName || "—";
		const last = row?.original?.coborrower_extra?.LastName || "";

		return (
			<div style={{ lineHeight: "1.2" }}>
				<div>{first}</div>
				<div>{last}</div>
			</div>
		);
	};

	// Vehicle details on three lines
	const vehicleCell = ({ row }) => {
		const v = row.original.loan_request_vehicle;
		return (
			<div style={{ lineHeight: "1.2" }}>
				<div>
					{v.VehicleYear} {v.Make}
				</div>
				<div>{v.Model}</div>
				<div>{v.Vin}</div>
			</div>
		);
	};

	// Term & Payment on two lines
	const termPaymentCell = ({ row }) => {
		const { Term: term, ReqPmt: pmt } = row.original.loan_request;
		return (
			<div style={{ lineHeight: "1.2" }}>
				<div>{term != null ? `${term} months` : ""}</div>
				<div>{pmt != null ? `$${pmt.toFixed(2)}/mo` : ""}</div>
			</div>
		);
	};

	// Render expanded row content showing full application data
	const renderRowSubComponent = ({ row }) => {
		const data = row.original;

		// Try to parse raw_callback_payload if it exists (might be a JSON string)
		let callbackData = null;
		if (data.raw_callback_payload) {
			try {
				callbackData = typeof data.raw_callback_payload === 'string'
					? JSON.parse(data.raw_callback_payload)
					: data.raw_callback_payload;
			} catch (e) {
				console.error("Failed to parse raw_callback_payload:", e);
			}
		}

		// Extract all the data fields (checking multiple sources)
		const Application = data.application_data || data.Application || callbackData?.Application;
		const Vehicle = data.vehicle_data || data.Vehicle || callbackData?.Vehicle;
		const LoanRequestVehicle = data.loan_request_vehicle;
		const DecisionLoanStructure = data.decision_loan_structure || data.DecisionLoanStructure || callbackData?.DecisionLoanStructure;
		const BookedLoanStructure = data.booked_loan_structure;
		const LoanRequest = data.loan_request;
		const DecisionStipulations = data.decision_stipulations || data.DecisionStipulations || callbackData?.DecisionStipulations;
		const VerificationStipulations = data.verification_stipulations || data.VerificationStipulations || callbackData?.VerificationStipulations;
		const DecisionAdverseActionReasons = data.decision_adverse_action_reasons || data.DecisionAdverseActionReasons || callbackData?.DecisionAdverseActionReasons;
		const DecisionNotes = data.decision_notes || data.DecisionNotes || callbackData?.DecisionNotes;
		const LenderNotes = data.lender_notes;

		// Helper to format currency
		const formatCurrency = (value) => {
			if (value == null || value === "") return "—";
			return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
		};

		// Helper to format date
		const formatDate = (dateStr) => {
			if (!dateStr) return "—";
			try {
				const date = new Date(dateStr);
				return date.toLocaleString("en-US", {
					year: "numeric",
					month: "short",
					day: "numeric",
					hour: "numeric",
					minute: "2-digit",
					hour12: true
				});
			} catch {
				return dateStr;
			}
		};

		// Helper to render info row
		const InfoRow = ({ label, value, highlight = false }) => (
			<div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
				<strong style={{ fontSize: "0.9rem" }}>{label}:</strong>
				<span style={{ fontSize: "0.9rem", fontWeight: highlight ? "600" : "400" }}>{value || "—"}</span>
			</div>
		);

		// Common card title style for left alignment
		const cardTitleStyle = { textAlign: "left" };

		return (
			<div style={{ width: "100%", padding: "1rem" }}>
				<div className="row">
					{/* Decision Notes */}
					{DecisionNotes && DecisionNotes.length > 0 && (
						<div className="col-md-12 mb-3">
							<Card>
								<CardHeader className="bg-success text-white">
									<CardTitle tag="h6" className="mb-0 text-white" style={cardTitleStyle}>Decision Notes</CardTitle>
								</CardHeader>
								<CardBody>
									{DecisionNotes.map((note, idx) => (
										<div key={idx} className="mb-2 p-2" style={{
											backgroundColor: "rgba(0,0,0,0.02)",
											borderRadius: "4px",
											fontSize: "0.85rem",
											fontFamily: "monospace",
											textAlign: "left"
										}}>
											{note}
										</div>
									))}
								</CardBody>
							</Card>
						</div>
					)}

					{/* Adverse Action Reasons */}
					{DecisionAdverseActionReasons && DecisionAdverseActionReasons.length > 0 && (
						<div className="col-md-12 mb-3">
							<Card>
								<CardHeader className="bg-secondary text-white">
									<CardTitle tag="h6" className="mb-0 text-white" style={cardTitleStyle}>Adverse Action Reasons</CardTitle>
								</CardHeader>
								<CardBody>
									<ul className="mb-0 pl-3" style={{ fontSize: "0.9rem", textAlign: "left" }}>
										{DecisionAdverseActionReasons.map((reason, idx) => (
											<li key={idx} className="mb-1" style={{ textAlign: "left" }}>{reason}</li>
										))}
									</ul>
								</CardBody>
							</Card>
						</div>
					)}

					{/* Verification Stipulations */}
					{VerificationStipulations && VerificationStipulations.length > 0 && (
						<div className="col-md-12 mb-3">
							<Card className="h-100">
								<CardHeader className="bg-warning">
									<CardTitle tag="h6" className="mb-0 card-title" style={cardTitleStyle}>Verification Stipulations ({VerificationStipulations.length})</CardTitle>
								</CardHeader>
								<CardBody>
									<ul className="mb-0 pl-3" style={{ fontSize: "0.9rem", textAlign: "left" }}>
										{VerificationStipulations.map((stip, idx) => (
											<li key={idx} className="mb-2" style={{ textAlign: "left" }}>{stip}</li>
										))}
									</ul>
								</CardBody>
							</Card>
						</div>
					)}

					{/* Application Details */}
					{Application && (
						<div className="col-md-6 mb-3">
							<Card className="h-100">
								<CardHeader className="bg-primary text-white">
									<CardTitle tag="h6" className="mb-0 text-white" style={cardTitleStyle}>Application Details</CardTitle>
								</CardHeader>
								<CardBody>
									<InfoRow label="Application #" value={Application.Appno} />
									<InfoRow label="Decision Status" value={Application.DecisionStatus} highlight />
									<InfoRow label="Decision Date" value={formatDate(Application.DecisionStatusDate)} />
									<InfoRow label="Underwriter" value={Application.UnderwriterFullName} />
									<InfoRow label="Underwriter Phone" value={Application.UnderwriterPhone} />
									<InfoRow label="Underwriter Email" value={Application.UnderwriterEmail} />
									{Application.FundingStatus && Application.FundingStatus !== "Undefined" && (
										<>
											<InfoRow label="Funding Status" value={Application.FundingStatus} />
											<InfoRow label="Funding Date" value={formatDate(Application.FundingStatusDate)} />
											{Application.FunderFullName && <InfoRow label="Funder" value={Application.FunderFullName} />}
										</>
									)}
								</CardBody>
							</Card>
						</div>
					)}

					{/* Vehicle Details */}
					{Vehicle && (
						<div className="col-md-6 mb-3">
							<Card className="h-100">
								<CardHeader className="bg-info text-white">
									<CardTitle tag="h6" className="mb-0 text-white" style={cardTitleStyle}>Vehicle Information</CardTitle>
								</CardHeader>
								<CardBody>
									<InfoRow label="Year" value={Vehicle.Year} />
									<InfoRow label="Make" value={Vehicle.Make} />
									<InfoRow label="Model" value={Vehicle.Model} />
									<InfoRow label="Trim" value={Vehicle.Trim} />
									<InfoRow label="VIN" value={Vehicle.Vin} />
									<InfoRow label="Mileage" value={Vehicle.Mileage ? Vehicle.Mileage.toLocaleString() : "—"} />
									<InfoRow label="Type" value={Vehicle.Type === "U" ? "Used" : Vehicle.Type === "N" ? "New" : Vehicle.Type} />
								</CardBody>
							</Card>
						</div>
					)}

					{/* Loan Structure */}
					{DecisionLoanStructure && Object.keys(DecisionLoanStructure).length > 0 && (
						<div className="col-md-6 mb-3">
							<Card className="h-100">
								<CardHeader style={{ backgroundColor: "#28a745", color: "white" }}>
									<CardTitle tag="h6" className="mb-0" style={{ color: "white", textAlign: "left" }}>Counter Offer Terms</CardTitle>
								</CardHeader>
								<CardBody>
									{Object.entries(DecisionLoanStructure).map(([key, value]) => {
										const label = key.replace(/([A-Z])/g, " $1").trim();
										const formattedValue = typeof value === "number" && key.toLowerCase().includes("price") || key.toLowerCase().includes("payment") || key.toLowerCase().includes("down")
											? formatCurrency(value)
											: value;
										return <InfoRow key={key} label={label} value={formattedValue} highlight />;
									})}
								</CardBody>
							</Card>
						</div>
					)}

					{/* Decision Stipulations */}
					{DecisionStipulations && DecisionStipulations.length > 0 && (
						<div className="col-md-6 mb-3">
							<Card className="h-100">
								<CardHeader className="bg-warning">
									<CardTitle tag="h6" className="mb-0 card-title" style={cardTitleStyle}>Decision Stipulations ({DecisionStipulations.length})</CardTitle>
								</CardHeader>
								<CardBody>
									<ul className="mb-0 pl-3" style={{ fontSize: "0.9rem" }}>
										{DecisionStipulations.map((stip, idx) => (
											<li key={idx} className="mb-2">{stip}</li>
										))}
									</ul>
								</CardBody>
							</Card>
						</div>
					)}

					{/* Lender Notes */}
					{LenderNotes && LenderNotes.length > 0 && (
						<div className="col-md-12 mb-3">
							<Card>
								<CardHeader className="bg-dark text-white">
									<CardTitle tag="h6" className="mb-0 text-white" style={cardTitleStyle}>Lender Notes</CardTitle>
								</CardHeader>
								<CardBody>
									{LenderNotes.map((note, idx) => (
										<div key={idx} className="mb-2 p-2" style={{
											backgroundColor: "rgba(0,0,0,0.02)",
											borderRadius: "4px",
											fontSize: "0.85rem",
											fontFamily: "monospace"
										}}>
											{note}
										</div>
									))}
								</CardBody>
							</Card>
						</div>
					)}

					{/* Loan Request */}
					{LoanRequest && Object.keys(LoanRequest).length > 0 && (
						<div className="col-md-6 mb-3">
							<Card className="h-100">
								<CardHeader style={{ backgroundColor: "#6c757d", color: "white" }}>
									<CardTitle tag="h6" className="mb-0" style={{ color: "white", textAlign: "left" }}>Original Loan Request</CardTitle>
								</CardHeader>
								<CardBody>
									{Object.entries(LoanRequest).map(([key, value]) => {
										const label = key.replace(/([A-Z])/g, " $1").trim();
										const isCurrency = key.toLowerCase().includes("price") ||
											key.toLowerCase().includes("pmt") ||
											key.toLowerCase().includes("down") ||
											key.toLowerCase().includes("amt") ||
											key.toLowerCase().includes("fee") ||
											key.toLowerCase().includes("tax") ||
											key.toLowerCase().includes("allow") ||
											key.toLowerCase().includes("rebate") ||
											key.toLowerCase().includes("debt");
										const formattedValue = typeof value === "number" && isCurrency
											? formatCurrency(value)
											: value;
										return <InfoRow key={key} label={label} value={formattedValue} />;
									})}
								</CardBody>
							</Card>
						</div>
					)}

					{/* Booked Loan Structure */}
					{BookedLoanStructure && Object.keys(BookedLoanStructure).length > 0 && (
						<div className="col-md-12 mb-3">
							<Card>
								<CardHeader style={{ backgroundColor: "#fd7e14", color: "white" }}>
									<CardTitle tag="h6" className="mb-0" style={{ color: "white", textAlign: "left" }}>Booked Loan Structure</CardTitle>
								</CardHeader>
								<CardBody>
									<div className="row">
										{Object.entries(BookedLoanStructure).map(([key, value]) => {
											const label = key.replace(/([A-Z])/g, " $1").trim();
											const isCurrency = typeof value === "number" && (
												key.toLowerCase().includes("price") ||
												key.toLowerCase().includes("payment") ||
												key.toLowerCase().includes("down") ||
												key.toLowerCase().includes("amt") ||
												key.toLowerCase().includes("fee")
											);
											const formattedValue = isCurrency ? formatCurrency(value) : value;
											return (
												<div key={key} className="col-md-6">
													<InfoRow label={label} value={formattedValue} />
												</div>
											);
										})}
									</div>
								</CardBody>
							</Card>
						</div>
					)}
				</div>
			</div>
		);
	};

	// Actions: View Deal (navigates via row click too) & Withdraw
	const actionCell = ({ row }) => {
		const status = row.original.status?.trim()?.toUpperCase();
		const isDenied = DENIED_STATUSES.includes(status);

		return (
			<div
				className="d-flex flex-column align-items-end"
				style={{ gap: "0.5rem" }}
			>
				{!hideViewDealButton && !isDenied ? (
					<Button
						color="secondary"
						size="sm"
						onClick={(e) => {
							e.stopPropagation();
							const dealIdToUse = row.original.deal_id;
							history.push(dealDetailPage, { ID: dealIdToUse });
						}}
					>
						View Deal
					</Button>
				) : isDenied ? (
					<Button
						color="danger"
						size="sm"
						onClick={(e) => {
							e.stopPropagation();
							confirmAndHardDeleteApplication(
								row.original.submitted_app_id,
								handleDeleteSuccess
							);
						}}
					>
						Delete
					</Button>
				) : (
					<Button
						color="info"
						size="sm"
						onClick={(e) => {
							e.stopPropagation();
							row.toggleRowExpanded();
						}}
					>
						{row.isExpanded ? "Hide Details" : "View Details"}
					</Button>
				)}
			</div>
		);
	};
	// Table columns (sortable, no filter dropdowns)
	const columns = [
		{
			Header: "Status",
			accessor: "status",
			id: "status",
			Cell: statusCell,
		},
		{
			Header: "Date",
			accessor: "created_date",
			id: "created_date",
			Cell: dateCell,
		},
		{
			Header: "Lender",
			accessor: "lender_id",
			id: "lender_id",
			Cell: lenderCell,
		},
		{
			Header: "Borrower",
			accessor: (row) => row.borrower_extra.FirstName,
			id: "borrower",
			Cell: borrowerCell,
		},
		{
			Header: "Co-Borrower",
			accessor: (row) => row.coborrower_extra?.FirstName || "—",
			id: "coborrower",
			Cell: coborrowerCell,
		},
		{
			Header: "Vehicle",
			accessor: (row) => row.loan_request_vehicle.Make,
			id: "vehicle",
			Cell: vehicleCell,
		},
		{
			Header: "Term & Payment",
			accessor: (row) => row.loan_request.Term,
			id: "term_payment",
			Cell: termPaymentCell,
		},
		{
			Header: "Actions",
			id: "actions",
			Cell: actionCell,
			disableSortBy: true,
		},
	];

	return (
		<>
			{tableData.length > 0 && (
				<div className="px-3">
					<div className="d-flex justify-content-end mb-2">
						<Button
							color={showDeniedOnly ? "danger" : "primary"}
							size="sm"
							disabled={!hasDeniedApps}
							onClick={() => setShowDeniedOnly((prev) => !prev)}
						>
							{showDeniedOnly
								? "Show Active Loans"
								: "Show Declined / Cancelled"}
						</Button>
					</div>

					<Card className="mt-2">
						<CardHeader className="pl-4 pt-3">
							<CardTitle tag="h3">📝 Submitted Loan Applications</CardTitle>
						</CardHeader>
						<CardBody>
							<ReactTable
								rowStyle={{ cursor: "default", width: "100%" }}
								data={tableData}
								columns={columns}
								renderRowSubComponent={renderRowSubComponent}
								initialState={{ sortBy: [{ id: "created_date", desc: true }] }}
							/>
						</CardBody>
					</Card>
				</div>
			)}
		</>
	);
};

LoansTable.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object).isRequired,
	hideViewDealButton: PropTypes.bool,
};

export default LoansTable;
